import { Component, computed, inject, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { bsIconTitleMap, BsIconTypes } from '@ui/elements/icon-bs/bs-icon-types';
import { get } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';


export type IconBsHandlers = {
    handlerClick?: <T>(body?: T) => void;
};

@Component({
    selector: 'ui-icon-bs',
    imports: [
        NgClass,
    ],
    templateUrl: './icon-bs.component.html',
    styleUrl: './icon-bs.component.scss'
})
export class IconBsComponent {
    /**
     * Pseudo link mode flag
     */
    public readonly pseudo: InputSignal<'white' | 'gray' | 'black' | 'success' | 'danger'> = input();
    /**
     * Additional link class
     */
    public readonly iconClass: InputSignal<string> = input('');
    /**
     * Icon code
     */
    public readonly type: InputSignal<BsIconTypes> = input();
    /**
     * Title attribute text
     */
    public readonly titleText: InputSignal<string> = input();
    /**
     * Accessible name; falls back to the title text. Required for clickable icons without a title
     */
    public readonly ariaLabel: InputSignal<string> = input();
    /**
     * Id of the element controlled by the icon (aria-controls)
     */
    public readonly ariaControls: InputSignal<string> = input();
    /**
     * Expanded state of the controlled element; rendered as true/false, omitted when not set
     */
    public readonly ariaExpanded: InputSignal<boolean> = input(undefined);
    /**
     * Font size icon in rem
     */
    public readonly fontSize: InputSignal<number> = input();
    /**
     * Disabled icon state flag
     */
    public readonly disabled: InputSignal<boolean> = input(false);
    /**
     * Icon actions handlers
     */
    public readonly handlers: InputSignal<IconBsHandlers> = input();
    /**
     * Click handler
     */
    public handlerClick: OutputEmitterRef<void> = output<void>();

    readonly #translate: TranslateService = inject(TranslateService);

    /**
     * Icon acts as a button (pseudo link mode or click handler passed)
     */
    public readonly interactive: Signal<boolean> = computed(() => !!this.pseudo() || !!this.handlers()?.handlerClick);

    public readonly accessibleName: Signal<string> = computed(() => this.ariaLabel() || this.titleTextMutate() || null);

    /**
     * Non-interactive icon without a name is purely decorative
     */
    public readonly decorative: Signal<boolean> = computed(() => !this.interactive() && !this.accessibleName());

    public onClick(): void {
        if (this.disabled()) {
            return;
        }
        this.handlers()?.handlerClick();
        this.handlerClick.emit();
    }

    /**
     * Enter/Space activate the icon like a native button, through a real click so host listeners
     * (e.g. mat-dialog-close) fire as well
     */
    public onKeyActivate(event: Event): void {
        if (!this.interactive() || this.disabled()) {
            return;
        }
        event.preventDefault();
        (event.target as HTMLElement).click();
    }

    public titleTextMutate: Signal<string> = computed(() => {
        const type = this.type();
        const titleText = this.titleText();

        if (titleText) {
            return titleText;
        }

        if (type) {
            const key: string = get(bsIconTitleMap, type, null);
            return key ? this.#translate.instant(get(bsIconTitleMap, type, null)) : null;
        }

        return '';
    });
}
